import { hasSession, supabase } from '../lib/supabase'
import type { Show } from '../types/portal'

/**
 * Loads the genuine shows from Supabase. Mirrors utils/projectsSource.ts's
 * shape exactly, one level up: a show has no deliverables of its own — it is
 * the identity (client, name, slug) plus the handful of fields — type,
 * description, client contact — shared across every episode, so a show
 * carries its own canonical copy of each rather than each episode holding
 * one that can drift out of step.
 */

function warn(what: string, error: { message: string } | null) {
  if (error) console.warn(`[supabase] ${what} failed: ${error.message}`)
}

function text(value: unknown): string {
  return value === null || value === undefined ? '' : String(value)
}

function storedShow(row: Record<string, unknown>): Show {
  return {
    id: String(row.id),
    slug: text(row.slug),
    client: text(row.client_name),
    name: text(row.show_name),
    status: text(row.status) || 'active',
    type: text(row.project_type),
    description: text(row.description),
    clientContact: text(row.client_contact),
  }
}

/**
 * Every show. Held back until a session exists — see the note on
 * fetchProjects() in utils/projectsSource.ts, which this mirrors.
 */
export async function fetchShows(): Promise<Show[]> {
  if (!supabase) return []
  if (!(await hasSession())) return []
  const { data, error } = await supabase.from('shows').select('*')
  warn('show list', error)
  return (data ?? []).map((row) => storedShow(row as Record<string, unknown>))
}

/** One show by its link slug — used when a client opens a show link. */
export async function fetchShowBySlug(slug: string): Promise<Show | null> {
  if (!supabase || !slug) return null
  const { data, error } = await supabase.from('shows').select('*').eq('slug', slug).maybeSingle()
  warn('show load', error)
  return data ? storedShow(data as Record<string, unknown>) : null
}

/** One show by its real id — used on the client side once join_show has granted access. */
export async function fetchShowById(showId: string): Promise<Show | null> {
  if (!supabase || !showId) return null
  const { data, error } = await supabase.from('shows').select('*').eq('id', showId).maybeSingle()
  warn('show load', error)
  return data ? storedShow(data as Record<string, unknown>) : null
}
